import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as NotifierActions from 'app/store/common-effects/notifier.effects';
import * as ProjectRoutingActions from 'app/store/common-effects/router.effects';
import { createProperties } from 'app/store/completed-entity-state/completed-entity-state';
import { CompletedEntityStateService } from 'app/store/completed-entity-state/completed-entity-state.service';
import { ScheduledTasksService } from 'core/http/project/scheduled-tasks/scheduled-tasks.service';
import { LoaderService } from 'core/services/loader.service';
import { exhaustMap, map, mapTo, switchMap, tap } from 'rxjs/operators';
import * as ScheduledTasksActions from './scheduled-tasks.actions';

@Injectable()
export class ScheduledTasksEffects {
  private readonly properties = createProperties(
    ScheduledTasksActions.setLoading,
    ScheduledTasksActions.markScheduledTasksLoaded,
    ScheduledTasksActions.setError,
    () => this.scheduledTasksService.getAllScheduledTasks(),
    'scheduledTasks',
  );

  getScheduledTasks$ = createEffect(() => this.actions$.pipe(
    ofType(ScheduledTasksActions.getScheduledTasks),
    exhaustMap(() => this.completedEntityStateService.get(this.properties)),
    map(tasks => ScheduledTasksActions.saveScheduledTasks({ tasks })),
  ));

  createScheduledTask$ = createEffect(() => this.actions$.pipe(
    ofType(ScheduledTasksActions.createScheduledTask),
    tap(() => this.loader.begin()),
    exhaustMap(({ input }) => this.scheduledTasksService.createScheduledTask(input)),
    tap(() => this.loader.end()),
    switchMap(entity => [
      ScheduledTasksActions.addScheduledTaskInStore({ entity }),
      NotifierActions.notify({ message: 'NOTIFICATIONS.scheduled_task_created' }),
      ProjectRoutingActions.navigateToProject({ path: ['setting', 'scheduled-tasks'] }),
    ]),
  ));

  updateScheduledTask$ = createEffect(() => this.actions$.pipe(
    ofType(ScheduledTasksActions.updateScheduledTask),
    tap(() => this.loader.begin()),
    exhaustMap(({ id, input }) => this.scheduledTasksService.editScheduledTask(id, input)),
    tap(() => this.loader.end()),
    switchMap(entity => [
      ScheduledTasksActions.updateScheduledTaskInStore({ update: { id: entity.id, changes: entity } }),
      NotifierActions.notify({ message: 'NOTIFICATIONS.scheduled_task_updated' }),
      ProjectRoutingActions.navigateToProject({ path: ['setting', 'scheduled-tasks'] }),
    ]),
  ));

  removeScheduledTask$ = createEffect(() => this.actions$.pipe(
    ofType(ScheduledTasksActions.removeScheduledTask),
    tap(() => this.loader.begin()),
    exhaustMap(({ id }) => this.scheduledTasksService.removeScheduledTask(id).pipe(mapTo(id))),
    tap(() => this.loader.end()),
    switchMap(id => [
      ScheduledTasksActions.removeScheduledTaskFromStore({ id }),
      NotifierActions.notify({ message: 'NOTIFICATIONS.scheduled_task_deleted' }),
    ]),
  ));

  constructor(
    private actions$: Actions,
    private scheduledTasksService: ScheduledTasksService,
    private loader: LoaderService,
    private completedEntityStateService: CompletedEntityStateService,
  ) {}
}
